<template>
  <div class="p-1">
    <v-card class="rounded-xl border border-slate-100 shadow-sm overflow-hidden" flat>
      <v-card-text class="p-6">
        <div class="flex flex-col gap-6">
          <!-- Title & Description Header -->
          <div class="flex items-start gap-4">
            <v-avatar color="indigo lighten-5" class="p-1" size="48">
              <v-icon color="indigo" size="28">mdi-email-outline</v-icon>
            </v-avatar>
            <div class="flex flex-col">
              <span class="text-sm font-bold text-slate-800 leading-tight">
                Mail Forwarding Integration
              </span>
              <span class="text-xs text-slate-500 mt-1 leading-normal">
                Map specific Use Cases and Sub Cases to target email addresses for automated email
                forwarding.
              </span>
            </div>
          </div>

          <v-divider class="border-slate-100"></v-divider>

          <!-- Configuration Form -->
          <div v-if="loading" class="py-12 text-center">
            <v-progress-circular indeterminate color="indigo" size="32"></v-progress-circular>
            <div class="text-slate-400 mt-2 font-medium text-xs">
              Loading integration settings...
            </div>
          </div>

          <div v-else class="flex flex-col gap-4">
            <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
              <!-- Use Case Selection -->
              <div>
                <label class="text-xs font-bold text-slate-700 mb-1 block">Use Case *</label>
                <v-select
                  v-model="config.useCase"
                  :items="useCaseOptions"
                  placeholder="Select Use Case"
                  outlined
                  dense
                  class="rounded-lg text-xs"
                  hide-details="auto"
                  @change="onUseCaseChange"
                ></v-select>
              </div>

              <!-- Sub Case Selection -->
              <div>
                <label class="text-xs font-bold text-slate-700 mb-1 block">Sub Case *</label>
                <v-select
                  v-model="config.subCase"
                  :items="filteredSubCases"
                  placeholder="Select Sub Case"
                  outlined
                  dense
                  class="rounded-lg text-xs"
                  hide-details="auto"
                  :disabled="!config.useCase"
                ></v-select>
              </div>

              <!-- Autogenerated Forwarding Email Field (Display only) -->
              <div class="md:col-span-2">
                <label class="text-xs font-bold text-slate-700 mb-1 block"
                  >Forwarding Email Address</label
                >
                <v-text-field
                  :value="autogeneratedEmail"
                  readonly
                  outlined
                  dense
                  filled
                  background-color="grey lighten-4"
                  class="rounded-lg text-xs"
                  hide-details="auto"
                  prepend-inner-icon="mdi-email-outline"
                ></v-text-field>
              </div>
            </div>

            <!-- Save Action Button -->
            <div class="flex justify-end gap-2 mt-4">
              <v-btn
                color="indigo"
                class="normal-case rounded-lg text-xs white--text"
                depressed
                @click="saveConfig"
                :loading="saving"
                :disabled="!config.useCase || !config.subCase"
              >
                <v-icon left size="16">mdi-content-save-outline</v-icon>
                Save Settings
              </v-btn>
            </div>
          </div>
        </div>
      </v-card-text>
    </v-card>
  </div>
</template>

<script>
export default {
  name: 'MailForwardingIntegration',
  props: {
    receivedData: {
      type: Object,
      default: null
    }
  },
  data() {
    return {
      loading: false,
      saving: false,
      subCasesList: [],
      useCaseOptionsList: [],
      config: {
        useCase: '',
        subCase: ''
      }
    }
  },
  computed: {
    useCaseOptions() {
      return this.useCaseOptionsList
    },
    filteredSubCases() {
      if (!this.config.useCase) return []
      return this.subCasesList
        .filter((sc) => String(sc.useCase).toUpperCase() === this.config.useCase)
        .map((sc) => ({
          text: sc.name,
          value: sc._id
        }))
    },
    autogeneratedEmail() {
      const orgData = this.$storage.getUniversal('orgData')
      const orgId = orgData?.id || ''
      return `complaints+${orgId}@inbound.myfinfi.com`
    }
  },
  async mounted() {
    await this.fetchAllowedUseCases()
    await this.fetchSubCases()
    await this.loadSavedConfig()
  },
  methods: {
    async fetchAllowedUseCases() {
      try {
        const res = await this.$axios.get(`/orgSetup/allowed-use-cases`)
        const list = res.data?.data || []
        this.useCaseOptionsList = list.map((u) => ({
          text: u.navName || u.useCase.charAt(0).toUpperCase() + u.useCase.slice(1).toLowerCase(),
          value: u.useCase.toUpperCase()
        }))
      } catch (error) {
        console.error('Error fetching allowed use cases:', error)
      }
    },
    async fetchSubCases() {
      this.loading = true
      try {
        const response = await this.$axios.get('/orgSetup/extend-usecase')
        this.subCasesList = response.data?.data || []
      } catch (error) {
        console.error('Error fetching sub cases:', error)
        this.$toast.error('Failed to load sub cases')
      } finally {
        this.loading = false
      }
    },
    async loadSavedConfig() {
      const saved = this.receivedData?.applications?.mailForwarding || {}
      this.config = {
        useCase: saved.useCase || '',
        subCase: saved.subCase || ''
      }
    },
    onUseCaseChange() {
      this.config.subCase = ''
    },
    async saveConfig() {
      this.saving = true
      try {
        const payload = {
          applications: {
            ...this.receivedData?.applications,
            mailForwarding: {
              ...this.receivedData?.applications?.mailForwarding,
              enable: true,
              useCase: this.config.useCase,
              subCase: this.config.subCase
            }
          }
        }
        await this.$axios.post('/org/config', payload)
        this.$emit('refetchData')
        this.$toast.success('Mail Forwarding Integration configured successfully')
      } catch (error) {
        console.error('Error saving mail forwarding configuration:', error)
        this.$toast.error('Failed to save configuration settings')
      } finally {
        this.saving = false
      }
    }
  }
}
</script>
